import React, { useEffect, useLayoutEffect, useMemo, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const TourOverlay = ({ isOpen, onClose, steps }) => {
  const [index, setIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const currentStep = steps?.[index] || null;

  const updateTargetRect = useCallback(() => {
    if (!isOpen || !currentStep?.selector) {
      setTargetRect(null);
      return;
    }

    const target = document.querySelector(currentStep.selector);
    if (!target) {
      setTargetRect(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    setTargetRect({
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height
    });
  }, [isOpen, currentStep]);

  useEffect(() => {
    if (isOpen) {
      setIndex(0);
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    updateTargetRect();
  }, [isOpen, index, updateTargetRect]);

  useEffect(() => {
    if (!isOpen) return;
    const handleUpdate = () => updateTargetRect();
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);
    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [isOpen, index, updateTargetRect]);

  const tooltipStyle = useMemo(() => {
    if (!targetRect) {
      return { top: '20%', left: '50%', transform: 'translateX(-50%)' };
    }

    const tooltipWidth = 320;
    const tooltipHeight = 180;
    const padding = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = targetRect.right + padding;
    let top = targetRect.top;

    if (left + tooltipWidth > viewportWidth - padding) {
      left = Math.max(padding, targetRect.left - tooltipWidth - padding);
    }

    if (top + tooltipHeight > viewportHeight - padding) {
      top = Math.max(padding, viewportHeight - tooltipHeight - padding);
    }

    return { top, left };
  }, [targetRect]);

  if (!isOpen || !steps?.length) return null;

  const isFirst = index === 0;
  const isLast = index === steps.length - 1;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/40" />

      {targetRect && (
        <div
          className="absolute border-2 border-bill-500 rounded-xl shadow-[0_0_0_9999px_rgba(32,30,29,0.4)]"
          style={{
            top: Math.max(0, targetRect.top - 6),
            left: Math.max(0, targetRect.left - 6),
            width: targetRect.width + 12,
            height: targetRect.height + 12
          }}
        />
      )}

      <div
        className="absolute w-80 bg-white border border-[rgba(32,30,29,0.12)] rounded-xl p-4 shadow-brand-lg"
        style={tooltipStyle}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs text-sand-600 mb-1">
              Step {index + 1} of {steps.length}
            </p>
            <h3 className="text-lg font-semibold text-ink">{currentStep?.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-sand-500 hover:text-ink transition-colors"
            aria-label="Close tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {currentStep?.description && (
          <p className="text-sm text-sand-700 mt-2">{currentStep.description}</p>
        )}

        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            onClick={() => setIndex((prev) => Math.max(0, prev - 1))}
            disabled={isFirst}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sand-100 text-sand-700 hover:bg-sand-200 text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-xs text-sand-600 hover:text-ink transition-colors"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={() => (isLast ? onClose() : setIndex((prev) => Math.min(steps.length - 1, prev + 1)))}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-bill-500 text-white hover:bg-bill-600 shadow-brand-sm text-xs transition-colors"
            >
              {isLast ? 'Finish' : 'Next'}
              {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourOverlay;
